import { CloudOff, NotebookPen, Pin, RefreshCw, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { TextLink } from "./kit";
import { NOTE_CATEGORIES } from "../lib/noteCategories";

/**
 * The player's coach-feedback list on the new look (#1157): the round-10
 * `CoachFeedbackForPlayer` board (D243, D254 Q7), rendered inside Plan's
 * More > Coach feedback fold. Presentational only: `DevelopmentPlan.jsx`
 * keeps the reads and the coach-feedback-seen write.
 *
 * Four states, never confused (D53 ruling 2, #271): failed is checked first
 * and is never an empty state; loading draws shapes and no words; then the
 * two empty states and the list. No gold anywhere on this view (board note).
 */

const NAVY = "#0B2E59";

function Shell({ children }) {
  return (
    <div className="flex flex-col" style={{ gap: 10 }} data-testid="player-coach-feedback">
      <div className="flex flex-col" style={{ gap: 6 }}>
        <div className="nw-eyebrow">Coach feedback</div>
        <div className="nw-title">From your coach</div>
        <div style={{ fontSize: 14, lineHeight: 1.5, color: "#475569" }}>
          What a coach you are linked to has written about your game. Pinned notes stay at the top.
        </div>
      </div>
      {children}
    </div>
  );
}

const CARD = { display: "flex", flexDirection: "column", gap: 8, padding: 16, background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12 };

function EmptyCard({ testId, icon, title, body, children }) {
  return (
    <div data-testid={testId} style={{ ...CARD, gap: 10, alignItems: "center", textAlign: "center", padding: "28px 20px" }}>
      <div data-testid="feedback-empty-icon-well" style={{ width: 52, height: 52, borderRadius: 16, background: "#E8F1FB", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {icon}
      </div>
      <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>{title}</div>
      <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink-muted)", maxWidth: 320 }}>{body}</div>
      {children}
    </div>
  );
}

function NoteRow({ n }) {
  const meta = NOTE_CATEGORIES[n.category] || NOTE_CATEGORIES.general;
  return (
    <div data-testid={`feedback-note-${n.note_id}`} style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600, borderRadius: 999, background: "#E8F1FB", color: NAVY, whiteSpace: "nowrap" }}>
          {meta.label}
        </span>
        {n.pinned && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", fontSize: 11, fontWeight: 700, borderRadius: 999, border: `1px solid ${NAVY}`, color: NAVY }}>
            <Pin size={11} color={NAVY} aria-hidden="true" /> Pinned
          </span>
        )}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{n.feedback}</div>
      <div style={{ fontSize: 12, color: "var(--nw-ink-muted)", marginTop: 8 }}>
        — {n.coach_name || "Your coach"} · {n.created_date?.slice(0, 10)}
      </div>
    </div>
  );
}

export default function PlayerCoachFeedback({ notes, hasCoach, failed, onRetry }) {
  // #271: first, before anything else. Neither empty state is an answer to a failed read.
  if (failed) {
    return (
      <Shell>
        <div data-testid="feedback-error" style={CARD}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <CloudOff size={20} color="var(--nw-alert)" strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0 }} />
            <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>Couldn't load coach feedback</div>
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>Nothing has been lost — we just couldn't reach the server.</div>
          <button
            type="button"
            onClick={onRetry}
            data-testid="feedback-error-retry"
            style={{ display: "inline-flex", alignSelf: "flex-start", alignItems: "center", justifyContent: "center", gap: 6, padding: "0 14px", minHeight: 44, background: "#FFFFFF", border: "1px solid #CBD5E1", color: NAVY, fontWeight: 600, fontSize: 14, borderRadius: 10, cursor: "pointer" }}
          >
            <RefreshCw size={14} color={NAVY} strokeWidth={2} aria-hidden="true" />
            Try again
          </button>
        </div>
      </Shell>
    );
  }

  if (notes.length > 0) {
    // Pinned first; Array#sort is stable, so the API's own order is kept inside each group.
    const sorted = [...notes].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
    return (
      <Shell>
        <div className="flex flex-col" style={{ gap: 10 }} data-testid="coach-feedback-list">
          {sorted.map((n) => <NoteRow key={n.note_id} n={n} />)}
        </div>
      </Shell>
    );
  }

  if (hasCoach === null) {
    return (
      <Shell>
        <div className="flex flex-col" style={{ gap: 8 }} data-testid="feedback-loading" aria-hidden="true">
          {[0, 1].map((i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8, padding: 14, height: 96, boxSizing: "border-box", borderRadius: 12, background: "#FFFFFF", border: "1px solid #E2E8F0" }}>
              <div style={{ width: "55%", height: 12, borderRadius: 6, background: "#E2E8F0" }} />
              <div style={{ width: "80%", height: 10, borderRadius: 5, background: "#F1F5F9" }} />
            </div>
          ))}
        </div>
      </Shell>
    );
  }

  if (hasCoach === false) {
    return (
      <Shell>
        <EmptyCard
          testId="feedback-empty-no-coach"
          icon={<Users size={22} color={NAVY} aria-hidden="true" />}
          title="You don't have a coach yet"
          body="Once a coach links to your profile, their feedback will show up here."
        >
          <TextLink as={Link} to="/settings" data-testid="feedback-empty-share-link" style={{ minHeight: 44, color: NAVY, fontWeight: 600, fontSize: 13, textDecoration: "underline" }}>
            Share your record with a coach from Settings
          </TextLink>
        </EmptyCard>
      </Shell>
    );
  }

  return (
    <Shell>
      <EmptyCard
        testId="feedback-empty-no-feedback"
        icon={<NotebookPen size={22} color={NAVY} aria-hidden="true" />}
        title="No coach feedback yet"
        body="Check back after your next session — new feedback will appear here."
      />
    </Shell>
  );
}
